import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Globe, RefreshCw, ExternalLink, Loader2, CheckCircle2,
  Play, ToggleLeft, ToggleRight, TrendingUp, MousePointerClick, Users, BarChart3,
  Plus, Terminal, ChevronDown, ChevronUp, DollarSign,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function pct(n) {
  if (n == null || isNaN(n)) return "-";
  return `${(n * 100).toFixed(1)}%`;
}

function pos(n) {
  if (n == null || isNaN(n) || n === 0) return "-";
  return n.toFixed(1);
}

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pl-PL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return "-"; }
}

export default function HuntedPortfolio() {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [expandedKw, setExpandedKw] = useState(null);
  const [keywords, setKeywords] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [newNiche, setNewNiche] = useState("");
  const [newArticles, setNewArticles] = useState(1);
  const [newSkipGsc, setNewSkipGsc] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployTasks, setDeployTasks] = useState([]);
  const [showTasks, setShowTasks] = useState(false);
  const [showLog, setShowLog] = useState(null);
  const [logText, setLogText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/hunted-portfolio/list`);
      setPortals(r.data.portals || []);
    } catch (e) {
      toast.error(`Błąd: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleAutoPublish(p) {
    setToggling(p.id);
    try {
      const r = await axios.patch(`${API}/admin/hunted-portfolio/${p.id}`,
        { auto_publish: !p.auto_publish });
      toast.success(`${p.name}: auto-publish ${r.data.updated.auto_publish ? "WŁ." : "WYŁ."}`);
      await load();
    } catch (e) {
      toast.error(`Błąd: ${e.response?.data?.detail || e.message}`);
    } finally {
      setToggling(null);
    }
  }

  async function publishNow(p) {
    setPublishing(p.id);
    try {
      const r = await axios.post(`${API}/admin/hunted-portfolio/${p.id}/publish-now`);
      toast.success(`${p.name}: cykl uruchomiony w tle — sprawdź za ~30-60s`);
    } catch (e) {
      toast.error(`Błąd: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPublishing(null);
    }
  }

  async function syncPortal(p) {
    setSyncing(p.id);
    try {
      await axios.post(`${API}/admin/hunted-portfolio/${p.id}/sync`);
      toast.success(`${p.name}: sync OK`);
      await load();
    } catch (e) {
      toast.error(`Błąd: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSyncing(null);
    }
  }

  async function deployNew() {
    if (!newDomain || !newDomain.match(/^[a-z0-9-]+\.[a-z]{2,7}$/i)) {
      toast.error("Podaj poprawną domenę (np. murarzradom.pl)");
      return;
    }
    setDeploying(true);
    try {
      const r = await axios.post(`${API}/admin/hunted-portfolio/deploy-new`, {
        domain: newDomain.toLowerCase(),
        niche: newNiche,
        articles_per_cat: newArticles,
        skip_gsc: newSkipGsc,
      });
      toast.success(`Deploy ${newDomain} uruchomiony (${r.data.task_id.slice(0,8)}...). ETA ~15 min.`);
      setShowNew(false);
      setNewDomain(""); setNewNiche(""); setNewArticles(1);
      await loadTasks();
      setShowTasks(true);
    } catch (e) {
      toast.error(`Błąd: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDeploying(false);
    }
  }

  async function loadTasks() {
    try {
      const r = await axios.get(`${API}/admin/hunted-portfolio/deploy-tasks`);
      setDeployTasks(r.data.tasks || []);
    } catch (e) {}
  }

  async function toggleKeywords(portalId) {
    if (expandedKw === portalId) {
      setExpandedKw(null);
      return;
    }
    setExpandedKw(portalId);
    if (!keywords[portalId]) {
      try {
        const r = await axios.get(`${API}/admin/hunted-portfolio/${portalId}/keywords`);
        setKeywords(prev => ({ ...prev, [portalId]: r.data.keywords || [] }));
      } catch (e) {
        toast.error(`Nie pobrano keywords: ${e.response?.data?.detail || e.message}`);
      }
    }
  }

  async function loadLog(taskId) {
    try {
      const r = await axios.get(`${API}/admin/hunted-portfolio/deploy-tasks/${taskId}/log?tail=300`);
      setLogText(r.data.log || "(brak logu)");
      setShowLog(taskId);
    } catch (e) {
      toast.error(`Błąd logu: ${e.response?.data?.detail || e.message}`);
    }
  }

  return (
    <div className="max-w-none p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="w-6 h-6 text-emerald-600" />
            Hunted Portfolio
          </h1>
          <p className="text-slate-600 text-sm">
            Portale z portfolio Aftermarket (VPS #2). Publikacja przez publisher API.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowNew(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-1" />
            Dodaj nowy portal
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadTasks(); setShowTasks(true); }}>
            <Terminal className="w-4 h-4 mr-1" />
            Zadania deploy
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Odśwież
          </Button>
        </div>
      </div>

      {/* Dialog: Deploy nowego portalu */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deploy nowego hunted portalu</DialogTitle>
            <DialogDescription>
              Jedno kliknięcie: DNS → deploy → SSL → GSC/GA4 → seed articles. ETA ~15 min.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Domena (musi być w portfolio Aftermarket)</label>
              <Input placeholder="murarzradom.pl" value={newDomain}
                onChange={e => setNewDomain(e.target.value)} />
              <p className="text-xs text-slate-500 mt-1">
                Jeśli nazwa zawiera branżę+miasto (murarz+radom, kebab+kolobrzeg, pizza+warszawa) —
                agent SEO wygeneruje lokalny ranking usług.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Nisza (opcjonalnie — Grok wykryje sam)</label>
              <Input placeholder="usługi budowlane" value={newNiche}
                onChange={e => setNewNiche(e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm">Artykułów per kategoria:</label>
              <Input type="number" min="1" max="5" value={newArticles}
                onChange={e => setNewArticles(parseInt(e.target.value) || 1)}
                className="w-20" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={newSkipGsc}
                onChange={e => setNewSkipGsc(e.target.checked)} />
              Pomiń GSC + GA4 (szybszy deploy, ale bez SEO/analytics)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Anuluj</Button>
            <Button onClick={deployNew} disabled={deploying || !newDomain}
              className="bg-emerald-600 hover:bg-emerald-700">
              {deploying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              Uruchom deploy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Zadania deploy */}
      <Dialog open={showTasks} onOpenChange={setShowTasks}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Zadania deploy (ostatnie 20)</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {deployTasks.length === 0 && <p className="text-slate-500 text-sm">Brak zadań.</p>}
            {deployTasks.map(t => (
              <div key={t.id} className="border rounded p-2 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm truncate">{t.domain}</div>
                  <div className="text-xs text-slate-500">
                    {fmtDate(t.started_at)} · {t.niche || "auto"} ·
                    <span className={
                      t.status === "completed" ? "text-emerald-600 ml-1" :
                        t.status === "running" ? "text-blue-600 ml-1" : "text-red-600 ml-1"}>
                      {t.status}
                    </span>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => loadLog(t.id)}>
                  <Terminal className="w-3 h-3 mr-1" /> Log
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={loadTasks}>
              <RefreshCw className="w-4 h-4 mr-1" /> Odśwież
            </Button>
            <Button onClick={() => setShowTasks(false)}>Zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Log */}
      <Dialog open={!!showLog} onOpenChange={() => setShowLog(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Log deploy: {showLog}</DialogTitle></DialogHeader>
          <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto max-h-96 whitespace-pre-wrap">
            {logText}
          </pre>
          <DialogFooter>
            <Button onClick={() => showLog && loadLog(showLog)}>
              <RefreshCw className="w-4 h-4 mr-1" /> Odśwież log
            </Button>
            <Button variant="outline" onClick={() => setShowLog(null)}>Zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading && portals.length === 0 && (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          <p className="text-sm text-slate-500 mt-2">Ładowanie...</p>
        </div>
      )}

      {portals.length === 0 && !loading && (
        <Card><CardContent className="py-8 text-center text-slate-500">
          Brak hunted portali. Uruchom sync z VPS #2 albo dodaj nowy portal przez <code>new_portal.py</code>.
        </CardContent></Card>
      )}

      <div className="grid gap-3">
        {portals.map(p => (
          <Card key={p.id} className={p.auto_publish ? "border-emerald-300" : ""}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt={p.name} width={40} height={40}
                      className="rounded bg-slate-100 p-1" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <CardTitle className="text-lg truncate">
                      <a href={p.url} target="_blank" rel="noopener" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                        {p.name} <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </CardTitle>
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <span className="font-mono">{p.domain}</span>
                      {p.niche && <span>· {p.niche}</span>}
                      {p.gsc_verified_at && <Badge variant="outline" className="bg-emerald-50 text-emerald-700">GSC ✓</Badge>}
                      {p.ga4_measurement_id && <Badge variant="outline" className="bg-orange-50 text-orange-700">GA4 {p.ga4_measurement_id}</Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={p.auto_publish ? "default" : "outline"}
                    onClick={() => toggleAutoPublish(p)}
                    disabled={toggling === p.id}
                    className={p.auto_publish ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  >
                    {toggling === p.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> :
                      p.auto_publish ? <ToggleRight className="w-4 h-4 mr-1" /> : <ToggleLeft className="w-4 h-4 mr-1" />}
                    Auto: {p.auto_publish ? "ON" : "OFF"}
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => publishNow(p)}
                    disabled={publishing === p.id}
                    title="Uruchom cykl generacji + publikacji teraz">
                    {publishing === p.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                    Publikuj
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => syncPortal(p)}
                    disabled={syncing === p.id} title="Ściągnij aktualne konfigi z hunted portalu">
                    {syncing === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid md:grid-cols-4 gap-3 text-sm">
                {/* Posts */}
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-xs uppercase font-semibold text-slate-500 flex items-center gap-1">
                    <BarChart3 className="w-3 h-3" /> Artykuły
                  </div>
                  <div className="mt-1">
                    <span className="text-2xl font-bold">{p.posts_on_portal ?? "?"}</span>
                    <span className="text-slate-500 text-xs ml-1">na portalu</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Epidemos: {p.posts_epidemos ?? 0} publ. · {p.posts_error ?? 0} błędy
                  </div>
                  {p.last_published_on_portal && (
                    <div className="text-xs text-slate-400 mt-1 truncate" title={p.last_published_on_portal.title}>
                      Ostatnio: {p.last_published_on_portal.title?.substring(0, 30)}...
                    </div>
                  )}
                </div>

                {/* GSC */}
                <div className="bg-emerald-50 rounded p-2">
                  <div className="text-xs uppercase font-semibold text-emerald-700 flex items-center gap-1">
                    <MousePointerClick className="w-3 h-3" /> Google Search
                  </div>
                  {p.gsc_stats ? (
                    <>
                      <div className="mt-1">
                        <span className="text-2xl font-bold text-emerald-700">{p.gsc_stats.clicks || 0}</span>
                        <span className="text-emerald-600 text-xs ml-1">kliknięć</span>
                      </div>
                      <div className="text-xs text-emerald-700 space-y-0.5 mt-1">
                        <div>{p.gsc_stats.impressions || 0} wyświetleń</div>
                        <div>CTR: {pct(p.gsc_stats.ctr)} · Poz.: {pos(p.gsc_stats.position)}</div>
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-slate-500 mt-2">Brak danych (dane pojawią się 1-3 dni po weryfikacji)</div>
                  )}
                </div>

                {/* GA4 */}
                <div className="bg-orange-50 rounded p-2">
                  <div className="text-xs uppercase font-semibold text-orange-700 flex items-center gap-1">
                    <Users className="w-3 h-3" /> Analytics
                  </div>
                  {p.ga4_stats ? (
                    <>
                      <div className="mt-1">
                        <span className="text-2xl font-bold text-orange-700">{p.ga4_stats.sessions || 0}</span>
                        <span className="text-orange-600 text-xs ml-1">sesji</span>
                      </div>
                      <div className="text-xs text-orange-700 space-y-0.5 mt-1">
                        <div>{p.ga4_stats.users || 0} użytkowników</div>
                        <div>Bounce: {pct(p.ga4_stats.bounce_rate)}</div>
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-slate-500 mt-2">Brak danych (włącz Analytics Data API)</div>
                  )}
                </div>

                {/* PageSpeed */}
                <div className="bg-purple-50 rounded p-2">
                  <div className="text-xs uppercase font-semibold text-purple-700 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> PageSpeed
                  </div>
                  {p.pagespeed ? (
                    <div className="mt-1 flex gap-1 flex-wrap text-xs">
                      <span className={`px-1.5 py-0.5 rounded font-bold ${(p.pagespeed.performance||0)>=90?"bg-emerald-200 text-emerald-800":(p.pagespeed.performance||0)>=50?"bg-amber-200 text-amber-800":"bg-red-200 text-red-800"}`}>P {p.pagespeed.performance ?? "?"}</span>
                      <span className={`px-1.5 py-0.5 rounded font-bold ${(p.pagespeed.accessibility||0)>=90?"bg-emerald-200 text-emerald-800":"bg-amber-200 text-amber-800"}`}>A {p.pagespeed.accessibility ?? "?"}</span>
                      <span className={`px-1.5 py-0.5 rounded font-bold ${(p.pagespeed.best_practices||0)>=90?"bg-emerald-200 text-emerald-800":"bg-amber-200 text-amber-800"}`}>BP {p.pagespeed.best_practices ?? "?"}</span>
                      <span className={`px-1.5 py-0.5 rounded font-bold ${(p.pagespeed.seo||0)>=90?"bg-emerald-200 text-emerald-800":"bg-amber-200 text-amber-800"}`}>SEO {p.pagespeed.seo ?? "?"}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 mt-2">Uruchom portfolio_qa.py</div>
                  )}
                </div>
              </div>

              {p.last_auto_run && (
                <div className="mt-3 text-xs text-slate-500 border-t pt-2">
                  Ostatni cykl auto: <strong>{fmtDate(p.last_auto_run)}</strong>
                  {p.last_auto_status && <> · status: <span className={p.last_auto_status === "completed" ? "text-emerald-600" : "text-red-600"}>{p.last_auto_status}</span></>}
                  {p.last_auto_topic && <> · temat: "{p.last_auto_topic}"</>}
                </div>
              )}

              {p.stats_error && (
                <div className="mt-2 text-xs text-red-600">
                  ⚠ Stats fetch fail: {p.stats_error}
                </div>
              )}

              {/* Money keywords rozwijane */}
              <div className="mt-3 border-t pt-2">
                <button onClick={() => toggleKeywords(p.id)}
                  className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  Money keywords ({keywords[p.id]?.length || "?"})
                  {expandedKw === p.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {expandedKw === p.id && (
                  <div className="mt-2 space-y-1 max-h-96 overflow-y-auto">
                    {!keywords[p.id] && <Loader2 className="w-4 h-4 animate-spin" />}
                    {keywords[p.id]?.map((k, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs p-2 rounded bg-slate-50">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{k.keyword}</div>
                          {k.suggested_title && (
                            <div className="text-slate-500 truncate">{k.suggested_title}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`px-1.5 py-0.5 rounded font-semibold ${
                            k.intent === "transactional" ? "bg-emerald-100 text-emerald-800" :
                            k.intent === "local" ? "bg-blue-100 text-blue-800" :
                            "bg-slate-200 text-slate-700"
                          }`}>{k.intent}</span>
                          <span className="font-bold">P{k.priority}</span>
                          {k.used && <Badge variant="secondary" className="text-[10px]">użyte</Badge>}
                          <span className={`font-mono ${
                            k.current_position && k.current_position <= 10 ? "text-emerald-600 font-bold" :
                            k.current_position && k.current_position <= 30 ? "text-amber-600" :
                            "text-slate-400"
                          }`}>
                            {k.current_position ? `#${k.current_position}` : "-"}
                          </span>
                        </div>
                      </div>
                    ))}
                    {keywords[p.id]?.length === 0 && (
                      <p className="text-xs text-slate-500">Brak money keywords. Uruchom money_keywords.py</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
